import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { EPoukazCreateInput } from '@libs/types/src/services/input-type/sukl/e-poukaz.create-input';
import { EPoukazUpdateInput } from '@libs/types/src/services/input-type/sukl/e-poukaz-update.input';
import { EPoukazCancelInput } from '@libs/types/src/services/input-type/sukl/e-poukaz-cancel.input';
import { EPoukazSyncInput } from '@libs/types/src/services/input-type/sukl/e-poukaz-sync.input';
import { EPoukazGridObject, EPoukazObject } from '@libs/types/src/services/object-type/sukl/e-poukaz.object';
import { EPoukazSyncObject } from '@libs/types/src/services/object-type/sukl/e-poukaz-sync.object';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from './types/filter.types';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { EPoukazDetailObject } from '@libs/types/src/services/object-type/sukl/e-poukaz-detail.object';
import { EPoukazAttachmentInput } from '@libs/types/src/services/input-type/sukl/e-poukaz-attachment.input';
import { EPoukazAddAttachmentInput } from '@libs/types/src/services/input-type/sukl/e-poukaz-add-attachment.input';
import {
    EPoukazAttachmentFileObject,
    EPoukazAttachmentObject,
} from '@libs/types/src/services/object-type/sukl/e-poukaz-attachment.object';

@Injectable({ providedIn: 'root' })
export class EPoukazGqlService extends GqlServiceV2 {
    public getEPoukaz(input: GridInput, fragment: string): Promise<EPoukazGridObject> {
        return this.query<EPoukazGridObject>(`
            query($input: GridInput!) {
                ePoukaz {
                    filter(input: $input) {
                        ${filterFrag}
                        items { ${fragment} }
                    }
                }
            }
        `, { input: {
            ...input,
            filter: input.filter?.map((group) => group.filter(({ column }) => column !== 'medicalDevice'))
                .filter((group) => group.length),
            sorter: input.sorter?.filter(({ column }) => column !== 'medicalDevice'),
        } });
    }

    public createEPoukaz(input: EPoukazCreateInput): Promise<EPoukazObject> {
        return this.mutate<EPoukazObject>(`
            mutation($input: EPoukazCreateInput!) {
                ePoukaz {
                    create(input: $input) {
                        id
                        suklId
                    }
                }
            }
        `, { input });
    }

    public updateEPoukaz(input: EPoukazUpdateInput): Promise<EPoukazObject> {
        return this.mutate<EPoukazObject>(`
            mutation($input: EPoukazUpdateInput!) {
                ePoukaz {
                    update(input: $input) {
                        id
                        suklId
                    }
                }
            }
        `, { input }, { offline: false });
    }

    public cancelEPoukaz(input: EPoukazCancelInput, fragment: string): Promise<EPoukazObject> {
        return this.mutate<EPoukazObject>(`
            mutation($input: EPoukazCancelInput!) {
                ePoukaz {
                    cancel(input: $input) { ${fragment} }
                }
            }
        `, { input });
    }

    public syncEPoukaz(input: EPoukazSyncInput): Promise<EPoukazSyncObject> {
        return this.mutate<EPoukazSyncObject>(`
            mutation($input: EPoukazSyncInput!) {
                ePoukaz {
                    sync(input: $input) {
                        dateFrom
                        dateTo
                        syncedAt
                        changedCount
                        synchronizedCount
                        unlinkedCount
                    }
                }
            }
        `, { input });
    }

    public getEPoukazDetail(input: IdInput, fragment: string): Promise<EPoukazDetailObject> {
        return this.query<EPoukazDetailObject>(`
            query($input: IdInput!) {
                ePoukaz {
                    detail(input: $input) { ${fragment} }
                }
            }
        `, { input });
    }

    public getEPoukazAttachments(input: IdInput): Promise<EPoukazAttachmentObject[]> {
        return this.query<EPoukazAttachmentObject[]>(`
            query($input: IdInput!) {
                ePoukaz {
                    attachments(input: $input) {
                        id
                        type
                        name
                        description
                        insurerCode
                        createdBy
                        createdAt
                    }
                }
            }
        `, { input });
    }

    public getEPoukazAttachment(input: EPoukazAttachmentInput): Promise<EPoukazAttachmentFileObject> {
        return this.query<EPoukazAttachmentFileObject>(`
            query($input: EPoukazAttachmentInput!) {
                ePoukaz {
                    attachment(input: $input) {
                        id
                        name
                        content
                    }
                }
            }
        `, { input }, { fetchPolicy: 'no-cache' });
    }

    public addEPoukazAttachment(input: EPoukazAddAttachmentInput): Promise<string> {
        return this.mutate<string>(`
            mutation($input: EPoukazAddAttachmentInput!) {
                ePoukaz {
                    addAttachment(input: $input)
                }
            }
        `, { input }, { offline: false });
    }
}
